import { useQuery, useInfiniteQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import {
  HotelSupplier,
  HotelDestinationResponse,
  HotelDestinationSearch,
  PagedResponse,
  VendorHotel,
  VendorHotelSearch,
  GetJobsParams,
  JobsListResponse,
  HotelBoard,
  HotelCategory,
  PagedAndSortedFilter,
  NimbuxHotel,
  NimbuxHotelSearch,
  PagedFilter,
} from '@bandido/shared/interfaces';

import { QueryKeys } from '@frontend/features/common/types';
import { client } from '@frontend/features/common/services';

import { useSnackBar } from '../common/components/SnackBarContext';

export const useGetHotelSuppliers = (params: PagedFilter) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelSuppliers],
    queryFn: async () => {
      const response = await client.get<PagedResponse<HotelSupplier>>('content-extractor/suppliers', {
        params,
      });
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotelContent.management.suppliers.errors.getSuppliers'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: true,
  });
};

export const useGetHotelNimbuxHotels = (params: NimbuxHotelSearch) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelNimbuxHotels, params],
    queryFn: async () => {
      const response = await client.get<PagedResponse<NimbuxHotel>>('content-extractor/nimbux-hotels', {
        params,
      });
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotelContent.nimbuxHotels.errors.getNimbuxHotels'),
          color: 'error',
        }),
    },
    enabled: params.id !== null,
    refetchOnWindowFocus: true,
  });
};

export const useInfiniteNimbuxHotels = (baseParams: Omit<NimbuxHotelSearch, 'currentPage'>) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useInfiniteQuery({
    queryKey: [QueryKeys.getHotelNimbuxHotels, 'infinite', baseParams],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await client.get<PagedResponse<NimbuxHotel>>('content-extractor/nimbux-hotels', {
        params: {
          ...baseParams,
          currentPage: pageParam,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.currentPage && lastPage.pages && lastPage.currentPage < lastPage.pages) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotelContent.nimbuxHotels.errors.getNimbuxHotels'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetAvailableCountries = () => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getAvailableCountries],
    queryFn: async (): Promise<string[]> => {
      const response = await client.get<string[]>('hotel/content/countries/list');
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotels.contentManager.error'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetHotelVendorHotels = (params: VendorHotelSearch) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelVendorHotels, params],
    queryFn: async () => {
      const response = await client.get<PagedResponse<VendorHotel>>('content-extractor/vendor-hotels', {
        params,
      });
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotelContent.management.vendorHotels.errors.getVendorHotels'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: true,
  });
};

export const useGetHotelDestinations = (params: HotelDestinationSearch) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelDestinations, params],
    queryFn: async () => {
      const response = await client.get<PagedResponse<HotelDestinationResponse>>('content-extractor/destinations', {
        params,
      });
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotelContent.management.destinations.errors.getDestinations'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: true,
  });
};

export const useGetHotelBoards = (params: PagedAndSortedFilter) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelBoards, params],
    queryFn: async () => {
      const response = await client.get<PagedResponse<HotelBoard>>('content-extractor/boards', {
        params,
      });
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotelContent.management.boards.errors.getBoards'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: true,
  });
};

export const useGetHotelCategories = (params: PagedAndSortedFilter) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelCategories, params],
    queryFn: async () => {
      const response = await client.get<PagedResponse<HotelCategory>>('content-extractor/categories', {
        params,
      });
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotelContent.management.categories.errors.getCategories'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: true,
  });
};

export const useGetJobsQuery = ({ page, limit, status }: GetJobsParams) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getContentExtractorJobs, { page, limit, status }],
    queryFn: async () => {
      const response = await client.get<JobsListResponse>('content-extractor/jobs', {
        params: { page, limit, status },
      });
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotelContent.jobs.errors.getJobs'),
          color: 'error',
        }),
    },
    refetchOnWindowFocus: true,
  });
};

export const useGetHotelVendorHotelById = (id?: number) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelVendorHotelById, id],
    queryFn: async () => {
      const response = await client.get<VendorHotel>(`content-extractor/vendor-hotels/${id}`);
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotelContent.management.vendorHotels.errors.getVendorHotelById'),
          color: 'error',
        }),
    },
    enabled: Boolean(id),
  });
};

export const useGetHotelNimbuxHotelById = (id?: number) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  return useQuery({
    queryKey: [QueryKeys.getHotelNimbuxHotelById, id],
    queryFn: async () => {
      const response = await client.get<NimbuxHotel>(`content-extractor/nimbux-hotels/${id}`);
      return response.data;
    },
    meta: {
      errorHandler: () =>
        addToast({
          message: t('hotelContent.nimbuxHotels.errors.getNimbuxHotelById'),
          color: 'error',
        }),
    },
    enabled: Boolean(id),
  });
};